<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.order.actions.editItem.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.saveChanges'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form v-if="item" ref="form">
        <o-field-group
          v-slot="{ status }"
          :label="$t('general.price')"
          :name="$t('general.price')"
          class="mb-4 block"
          :rules="isAmountTypePercentage ? 'between:0,100' : 'currency'"
          :validation-value="item.price"
        >
          <currency-field v-if="isAmountTypeFixed" v-model="item.price" :status="status" />
          <number-field
            v-if="isAmountTypePercentage"
            v-model="item.price"
            :min="0"
            :max="100"
            slim
            :status="status"
            trailing="%"
          />
        </o-field-group>

        <o-field-group
          v-if="editShowDiscount"
          v-slot="{ status }"
          :label="$t('general.discount')"
          :name="$t('general.discount')"
          class="mb-4 block"
          :rules="isDiscountTypePercentage ? 'between:0,100' : 'currency'"
        >
          <div class="flex items-center space-x-2">
            <currency-field v-if="isDiscountTypeFixed" v-model="item.discount" class="w-1/2" :status="status" />
            <number-field
              v-if="isDiscountTypePercentage"
              v-model="item.discount"
              trailing="%"
              :min="0"
              :max="100"
              slim
              class="w-1/2"
              :status="status"
            />
            <segment-field v-model="item.discountType" :options="itemDiscountTypeOptions" class="border w-1/2" />
          </div>
        </o-field-group>

        <o-field-group
          v-if="editShowRemoveVoucher && isVoucherApplied && discountChanged"
          :name="$t('components.order.actions.editItem.voucherOptions')"
          class="mb-4 block p-2 pb-1 border rounded-md dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
        >
          <checkbox-field v-model="removeVoucher" :label="$t('components.order.actions.editItem.removeVoucher', { voucher: item.voucher.name, code: item.voucher.code })" />
        </o-field-group>

        <o-field-group
          v-if="editShowTaxes"
          :label="$t('components.order.actions.editItem.discountTaxType')"
          :name="$t('components.order.actions.editItem.discountTaxType')"
          class="mb-4 block"
        >
          <segment-field v-model="item.discountTaxType" :options="itemDiscountTaxTypeOptions" />
        </o-field-group>

        <o-field-group
          v-if="editShowQuantity"
          v-slot="{ status }"
          :label="$t('general.quantity')"
          :name="$t('general.quantity')"
          class="mb-4 block"
        >
          <number-field v-model="item.quantity" :status="status" />
        </o-field-group>

        <o-field-group
          v-if="editShowEmployee"
          :label="$t('general.employee')"
          :name="$t('general.employee')"
          class="mb-4 block"
        >
          <employee-select v-model="item.employee" :types="['employee', 'admin']" class="w-full" />
        </o-field-group>

        <o-field-group
          v-if="item.title"
          v-slot="{ status }"
          :label="$t('general.title')"
          :name="$t('general.title')"
          class="mb-4 block"
          rules="required"
        >
          <text-field v-model="item.title" :status="status" />
        </o-field-group>

        <o-field-group
          v-if="$features.get('loyalty') && editShowLoyalty"
          v-slot="{ status }"
          :label="$t('general.loyaltyEnabled')"
          :name="$t('general.loyaltyEnabled')"
          class="mb-4 block"
        >
          <toggle-field v-model="item.loyaltyEnabled" :status="status" />
        </o-field-group>

        <template v-if="editShowTaxes">
          <tax-select v-model="item.taxIDs" class="w-full mb-4" />
        </template>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { OrderItemDiscountType, OrderItemDiscountTaxType } from '@/_api/models/orderItem'

import EmployeeSelect from '@/components/employee/elements/Select.vue'
import TaxSelect from '@/components/tax/elements/Select.vue'
import {
  TextField,
  NumberField,
  ToggleField,
  SegmentField,
  CurrencyField,
  CheckboxField,
} from '@/components/fields'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification } = useNuxtApp()
const { t } = useI18n()

const dialog = ref<any>()
const form = ref<any>()

const item = ref<any>(null)
const taxIDs = ref<any[]>([])
const originalDiscount = ref<any>(null)
const originalDiscountType = ref<any>(null)
const removeVoucher = ref(false)

const info = computed(() => {
  return item.value ? item.value.displayLabel : 'Item'
})

const editShowQuantity = computed(() => item.value?.editShowQuantity)

const editShowAmountType = computed(() => item.value?.editShowAmountType)

const editShowLoyalty = computed(() => item.value?.editShowLoyalty)

const editShowTaxes = computed(() => item.value?.editShowTaxes)

const editShowEmployee = computed(() => item.value?.editShowEmployee)

const editShowDiscount = computed(() => item.value?.editShowDiscount)

const editShowRemoveVoucher = computed(() => item.value?.editShowRemoveVoucher)

const isVoucherApplied = computed(() => !!item.value?.voucher)

const isDiscountTypeFixed = computed(() => item.value?.isDiscountTypeFixed)

const isDiscountTypePercentage = computed(() => item.value?.isDiscountTypePercentage)

const isAmountTypeFixed = computed(() => item.value?.isAmountTypeFixed)

const isAmountTypePercentage = computed(() => item.value?.isAmountTypePercentage)

const itemDiscountTypeOptions = computed(() => {
  return [
    { label: t('general.fixed'), value: OrderItemDiscountType.fixed },
    { label: t('general.percent'), value: OrderItemDiscountType.percentage },
  ]
})

const itemDiscountTaxTypeOptions = computed(() => {
  return [
    { label: t('components.order.actions.editItem.afterTax'), value: OrderItemDiscountTaxType.post },
    { label: t('components.order.actions.editItem.beforeTax'), value: OrderItemDiscountTaxType.pre },
  ]
})

const discountChanged = computed(() => {
  if (!item.value) { return false }
  return item.value.discount !== originalDiscount.value || item.value.discountType !== originalDiscountType.value
})

watch(() => item.value?.employee, (employee: any) => {
  if (item.value) {
    item.value.idEmployee = employee?.idEmployee
  }
})

function open (itemVal: any) {
  item.value = itemVal.copy()
  item.value.price = item.value.priceWithIncludedTaxes

  // Store original discount values for change detection
  originalDiscount.value = item.value.discount
  originalDiscountType.value = item.value.discountType
  removeVoucher.value = false

  dialog.value.open()
}

async function confirm () {
  emit('update:loading', true)

  try {
    const itemVal = item.value
    const response = await $api.order.saveItem(String(props.order.idOrder), itemVal, { removeVoucher: removeVoucher.value })
    const order = response.data

    emit('update:order', order)
    emit('update:loading', false)

    $notification.toast({
      title: t('components.order.actions.editItem.success'),
      text: t('components.order.actions.editItem.successInfo'),
      variant: 'success',
    })

    return true
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

function validate () {
  return form.value?.validate()
}

defineExpose({ open })
</script>
